import React from 'react';
import { Grid, Typography } from '@mui/material';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { useTranslation } from '../../contexts/translation';
import { HomePageBlock } from './HomePageBlock';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import * as api from '../../api/app/dashboardApi';
import { useQuery } from 'react-query';
import { DashboardTypes } from '../../types/app/dashboard.types';
import * as assetApi from '../../api/app/assetApi';
import * as monitoredCounterApi from '../../api/app/monitoredCounterApi';
import * as monitoredCounterScrapApi from '../../api/app/monitoredCounterScrapApi';
import * as monitoredCycleTimeApi from '../../api/app/monitoredCycleTimeApi';
import * as monitoredProcessDataApi from '../../api/app/monitoredProcessDataApi';
import * as monitoredStateApi from '../../api/app/monitoredStateApi';
import { useTheme } from '@mui/material/styles';
import HttpIcon from '@mui/icons-material/Http';
import BarChartIcon from '@mui/icons-material/BarChart';

export function HomePage() {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    data: assetCount,
    isLoading: assetIsLoading,
    isError: assetIsError,
  } = useQuery(['assetCount'], () => assetApi.getCount(false));

  const {
    data: assetCountTopOnly,
    isLoading: assetTopOnlyIsLoading,
    isError: assetTopOnlyIsError,
  } = useQuery(['assetCountTop'], () => assetApi.getCount(true));

  const {
    data: monitoredCounterCount,
    isLoading: monitoredCounterIsLoading,
    isError: monitoredCounterIsError,
  } = useQuery(['monitoredCounterCount'], () => monitoredCounterApi.getCount());

  const {
    data: monitoredCounterScrapCount,
    isLoading: monitoredCounterScrapIsLoading,
    isError: monitoredCounterScrapIsError,
  } = useQuery(['monitoredCounterScrapCount'], () => monitoredCounterScrapApi.getCount());

  const {
    data: monitoredCycleTimeCount,
    isLoading: monitoredCycleTimeIsLoading,
    isError: monitoredCycleTimeIsError,
  } = useQuery(['monitoredCycleTimeCount'], () => monitoredCycleTimeApi.getCount());

  const {
    data: monitoredProcessDataCount,
    isLoading: monitoredProcessDataIsLoading,
    isError: monitoredProcessDataIsError,
  } = useQuery(['monitoredProcessDataCount'], () => monitoredProcessDataApi.getCount());

  const {
    data: monitoredStateCount,
    isLoading: monitoredStateIsLoading,
    isError: monitoredStateIsError,
  } = useQuery(['monitoredStateCount'], () => monitoredStateApi.getCount());

  const {
    data: countData,
    isLoading: countDataIsLoading,
    isError: countDataIsError,
  } = useQuery(['countData'], () => api.getCount());
  const {
    data: countDataMain,
    isLoading: countDataMainIsLoading,
    isError: countDataMainIsError,
  } = useQuery(['countDataMain'], () => api.getCount(DashboardTypes.MAIN_DASHBOARD));
  const {
    data: countDataOpc,
    isLoading: countDataOpcIsLoading,
    isError: countDataOpcIsError,
  } = useQuery(['countDataOpc'], () => api.getCount(DashboardTypes.OPC_DASHBOARD));
  const {
    data: countDataGrafana,
    isLoading: countDataGrafanaIsLoading,
    isError: countDataGrafanaIsError,
  } = useQuery(['countDataGrafana'], () => api.getCount(DashboardTypes.GRAFANA));
  const {
    data: countDataExternal,
    isLoading: countDataExternalIsLoading,
    isError: countDataExternalIsError,
  } = useQuery(['countDataExternal'], () => api.getCount(DashboardTypes.EXTERNAL_URL));

  if (
    assetIsError ||
    assetTopOnlyIsError ||
    monitoredCounterIsError ||
    monitoredCounterScrapIsError ||
    monitoredCycleTimeIsError ||
    // monitoredProcessDataIsError ||
    monitoredStateIsError ||
    countDataIsError ||
    countDataMainIsError ||
    countDataOpcIsError ||
    countDataGrafanaIsError ||
    countDataExternalIsError
  )
    return <ApiError />;
  if (
    assetIsLoading ||
    assetTopOnlyIsLoading ||
    monitoredCounterIsLoading ||
    monitoredCounterScrapIsLoading ||
    monitoredCycleTimeIsLoading ||
    monitoredProcessDataIsLoading ||
    monitoredStateIsLoading ||
    countDataIsLoading ||
    countDataMainIsLoading ||
    countDataOpcIsLoading ||
    countDataGrafanaIsLoading ||
    countDataExternalIsLoading ||
    assetCount === undefined ||
    assetCountTopOnly === undefined ||
    monitoredCounterCount === undefined ||
    monitoredCounterScrapCount === undefined ||
    monitoredCycleTimeCount === undefined ||
    monitoredProcessDataCount === undefined ||
    monitoredStateCount === undefined ||
    countData === undefined ||
    countDataExternal === undefined ||
    countDataGrafana === undefined ||
    countDataMain === undefined ||
    countDataOpc === undefined
  )
    return <Loader />;

  const httpIcon = (
    <HttpIcon
      sx={{
        fontSize: '4em',
        textAlign: 'center',
        marginTop: '10px',
        color: theme.palette.primary.main,
      }}
    />
  );

  const grafanaIcon = (
    <img
      src={'/res/logo/grafana.svg'}
      alt={'grafana logo'}
      title={'grafana logo'}
      style={{
        fontSize: '3.5em',
        textAlign: 'center',
        marginTop: '10px',
        color: theme.palette.info.main,
        width: '25%',
      }}
    />
  );
  const opcIcon = (
    <img
      src={'/res/logo/opc.png'}
      alt={'grafana logo'}
      title={'grafana logo'}
      style={{
        fontSize: '3.5em',
        textAlign: 'center',
        marginTop: '10px',
        color: theme.palette.info.main,
        width: '25%',
      }}
    />
  );
  const mainIcon = (
    <BarChartIcon
      sx={{
        fontSize: '4em',
        textAlign: 'center',
        marginTop: '10px',
        color: theme.palette.primary.main,
      }}
    />
  );

  return (
    <>
      <PageContent>
        <Typography variant={'h4'} align="center">
          {t('page.home.main-headline')}
        </Typography>
        <Grid container spacing={2} mt={4} mb={2}>
          <Grid item xs={12}>
            <Typography variant={'h5'} align="left">
              {t('page.home.box-headline-group-1')}
            </Typography>
          </Grid>
          <HomePageBlock title={t('page.home.box-headline-1')} value={countData} />
          <HomePageBlock
            title={t('page.home.box-headline-2')}
            value={countDataMain}
            icon={mainIcon}
          />
          <HomePageBlock
            title={t('page.home.box-headline-opc')}
            value={countDataOpc}
            icon={opcIcon}
          />
          <HomePageBlock
            title={t('page.home.box-headline-3')}
            value={countDataGrafana}
            icon={grafanaIcon}
          />
          <HomePageBlock
            title={t('page.home.box-headline-4')}
            value={countDataExternal}
            icon={httpIcon}
          />
        </Grid>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12}>
            <Typography variant={'h5'} align="left">
              {t('page.home.box-headline-group-2')}
            </Typography>
          </Grid>
          <HomePageBlock title={t('page.home.box-headline-5')} value={assetCount} />
          <HomePageBlock title={t('page.home.box-headline-6')} value={assetCountTopOnly} />
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant={'h5'} align="left">
              {t('page.home.box-headline-group-3')}
            </Typography>
          </Grid>
          <HomePageBlock title={t('page.home.box-headline-7')} value={monitoredCounterCount} />
          <HomePageBlock title={t('page.home.box-headline-8')} value={monitoredCounterScrapCount} />
          <HomePageBlock title={t('page.home.box-headline-9')} value={monitoredCycleTimeCount} />
          <HomePageBlock title={t('page.home.box-headline-10')} value={monitoredProcessDataCount} />
          <HomePageBlock title={t('page.home.box-headline-11')} value={monitoredStateCount} />
        </Grid>
      </PageContent>
    </>
  );
}
